import { useState, useEffect } from 'react';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8787';

interface RemovalRequest {
  id: string;
  url: string;
  email: string;
  reason: string | null;
  status: string;
  verification_method: string | null;
  created_at: string;
  updated_at: string | null;
}

interface Pagination {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

function Removals() {
  const [requests, setRequests] = useState<RemovalRequest[]>([]);
  const [pagination, setPagination] = useState<Pagination>({
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState('');

  useEffect(() => {
    fetchRemovals();
  }, [pagination.page, statusFilter]);

  const fetchRemovals = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: pagination.page.toString(),
        limit: pagination.limit.toString(),
      });

      if (statusFilter) params.set('status', statusFilter);

      const response = await fetch(`${API_BASE_URL}/admin/removals?${params}`);
      if (!response.ok) throw new Error('Failed to fetch removals');
      
      const data = await response.json();
      setRequests(data.requests || []);
      setPagination(data.pagination);
    } catch (err) {
      setError('Failed to load removal requests');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleAction = async (id: string, action: 'approve' | 'reject') => {
    if (!confirm(`Are you sure you want to ${action} this removal request?`)) {
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/admin/removals/${id}/${action}`, {
        method: 'POST',
      });

      if (!response.ok) throw new Error(`Failed to ${action} removal`);

      fetchRemovals();
    } catch (err) {
      alert(`Failed to ${action} removal request`);
      console.error(err);
    }
  };

  const handlePageChange = (newPage: number) => {
    setPagination((prev) => ({ ...prev, page: newPage }));
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'pending_verification':
        return 'badge-warning';
      case 'approved':
        return 'badge-success';
      case 'rejected':
        return 'badge-error';
      default:
        return 'badge-warning';
    }
  };

  return (
    <div className="admin-page">
      <div className="admin-page-header">
        <h1>Removal Requests</h1>
        <p>Review and process content removal requests</p>
      </div>

      {/* Filters */}
      <div className="filter-bar">
        <select
          value={statusFilter}
          onChange={(e) => {
            setStatusFilter(e.target.value);
            setPagination((prev) => ({ ...prev, page: 1 }));
          }}
        >
          <option value="">All statuses</option>
          <option value="pending_verification">Pending</option>
          <option value="approved">Approved</option>
          <option value="rejected">Rejected</option>
        </select>
        <button className="btn btn-secondary" onClick={fetchRemovals}>
          Refresh
        </button>
      </div>

      {/* Results */}
      {loading ? (
        <div className="loading-state">Loading removal requests...</div>
      ) : error ? (
        <div className="empty-state">
          <p>{error}</p>
          <button className="btn btn-primary mt-4" onClick={fetchRemovals}>
            Retry
          </button>
        </div>
      ) : requests.length === 0 ? (
        <div className="card empty-state">
          <p>No removal requests found</p>
          <p className="text-secondary mt-2">
            {statusFilter ? 'Try changing the status filter.' : 'No users have requested content removal yet.'}
          </p>
        </div>
      ) : (
        <>
          <table className="data-table">
            <thead>
              <tr>
                <th>URL</th>
                <th>Requester</th>
                <th>Reason</th>
                <th>Status</th>
                <th>Verification</th>
                <th>Created</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {requests.map((request) => (
                <tr key={request.id}>
                  <td className="truncate">
                    <a href={request.url} target="_blank" rel="noopener noreferrer">
                      {request.url}
                    </a>
                  </td>
                  <td>{request.email}</td>
                  <td className="truncate">{request.reason || '-'}</td>
                  <td>
                    <span className={`badge ${getStatusBadge(request.status)}`}>
                      {request.status.replace('_', ' ')}
                    </span>
                  </td>
                  <td>{request.verification_method || '-'}</td>
                  <td>{new Date(request.created_at).toLocaleDateString()}</td>
                  <td>
                    {request.status === 'pending_verification' && (
                      <div className="actions">
                        <button
                          className="btn btn-primary btn-sm"
                          onClick={() => handleAction(request.id, 'approve')}
                        >
                          Approve
                        </button>
                        <button
                          className="btn btn-secondary btn-sm"
                          onClick={() => handleAction(request.id, 'reject')}
                        >
                          Reject
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* Pagination */}
          <div className="pagination">
            <button
              className="pagination-btn"
              disabled={pagination.page <= 1}
              onClick={() => handlePageChange(pagination.page - 1)}
            >
              Previous
            </button>
            <span className="pagination-info">
              Page {pagination.page} of {pagination.totalPages} ({pagination.total} requests)
            </span>
            <button
              className="pagination-btn"
              disabled={pagination.page >= pagination.totalPages}
              onClick={() => handlePageChange(pagination.page + 1)}
            >
              Next
            </button>
          </div>
        </>
      )}
    </div>
  );
}

export default Removals;
